import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { API_BASE_URL as baseUri } from "config/env";
import { AcceptTermsResponse, TermsResponse, termsInitialValue } from "./terms-interfaces";

// 'Slice/' prefix is load-bearing — store.ts routes only those actions through
// TokenRefreshMiddleware. A different prefix silently opts out of token refresh on a 401.

/**
 * Returns 200 with a null body when no terms version is in force yet. That is not an error:
 * it means the gate simply does not apply.
 */
export const GetCurrentTerms = createAsyncThunk<TermsResponse | null, void, { rejectValue: RejectedValue }>(
    'Slice/TermsSlice/GetCurrentTerms',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/terms-and-conditions/current', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const text = await response.text();
            return text ? (JSON.parse(text) as TermsResponse) : null;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

/**
 * The version is sent explicitly and the server rejects anything that is not the version
 * currently in force — a tab left open across a terms update must not be able to accept text
 * the user was never shown.
 */
export const AcceptTerms = createAsyncThunk<AcceptTermsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/TermsSlice/AcceptTerms',
    async (version, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/terms-and-conditions/accept', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({ version }),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as AcceptTermsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const TermsSlice = createSlice({
    name: 'termsSlice',
    initialState: termsInitialValue,
    reducers: {
        resetTermsSlice: () => termsInitialValue,
    },
    extraReducers(builder) {
        builder.addCase(GetCurrentTerms.pending, (state) => {
            state.isLoading = true;
            state.loadError = '';
        })
            .addCase(GetCurrentTerms.fulfilled, (state, action: PayloadAction<TermsResponse | null>) => {
                state.isLoading = false;
                state.current = action.payload;
            })
            .addCase(GetCurrentTerms.rejected, (state, action) => {
                state.isLoading = false;
                state.loadError = action.payload?.message ?? 'Error loading terms and conditions';
            })

            .addCase(AcceptTerms.pending, (state) => {
                state.isAccepting = true;
                state.acceptError = '';
            })
            .addCase(AcceptTerms.fulfilled, (state) => {
                state.isAccepting = false;
                if (state.current !== null) {
                    state.current.isAccepted = true;
                }
            })
            .addCase(AcceptTerms.rejected, (state, action) => {
                // The modal stays open and the user can press confirm again. It must not close
                // on a failed write: that would let them into the app un-accepted.
                state.isAccepting = false;
                state.acceptError = action.payload?.message ?? 'Error accepting terms and conditions';
            })
    }
});

export const { resetTermsSlice } = TermsSlice.actions;
export default TermsSlice.reducer;
